import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LinkIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationIcon,
  ServerIcon,
  UsersIcon,
  GlobeIcon,
  CogIcon,
  DownloadIcon,
  BadgeCheckIcon,
  ClockIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  DocumentTextIcon,
  KeyIcon,
  CubeIcon,
  FingerPrintIcon,
} from "@heroicons/react/solid";
import Gauge from "../Gauge/Gauge";

export default function TeamsAppDetail() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [activeTab, setActiveTab] = useState("overview");

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Fetch app details
  const { data: appData, isLoading, error } = useQuery(
    ["teams-app-detail", appId],
    async () => {
      const token = localStorage.getItem("tokenmob");
      const response = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/teams-apps/${appId}`,
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      return response.data;
    },
    {
      enabled: !!appId,
    }
  );

  // Fetch latest scan details with findings
  const { data: scanData } = useQuery(
    ["teams-app-latest-scan", appId],
    async () => {
      const token = localStorage.getItem("tokenmob");
      const response = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/teams-apps/${appId}/scans/latest`,
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );
      return response.data;
    },
    {
      enabled: !!appId,
    }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-500">Loading Teams app details...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading Teams app
            </h3>
            <p className="mt-2 text-sm text-red-700">
              {error.response?.data?.error?.message || error.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!appData?.app) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500">Teams app not found</div>
      </div>
    );
  }

  const app = appData.app;
  const latestScan = appData.latestScan || scanData?.scan;

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const getRiskColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getRiskBg = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'bg-red-100';
      case 'medium': return 'bg-yellow-100'; 
      case 'low': return 'bg-green-100';
      default: return 'bg-gray-100';
    }
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case "A*":
      case "A":
        return "text-green-600 bg-green-100";
      case "B+":
      case "B":
        return "text-yellow-600 bg-yellow-100";
      case "C":
        return "text-orange-600 bg-orange-100";
      case "D":
      case "F":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPermissionRisk = (permission) => {
    const highRiskPermissions = ['MessageTeamMembers', 'MessageChannelMembers', 'MessageAllUsers'];
    const mediumRiskPermissions = ['Identity', 'MessageTeamMembers'];
    
    if (highRiskPermissions.includes(permission)) return 'High';
    if (mediumRiskPermissions.includes(permission)) return 'Medium';
    return 'Low';
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: DocumentTextIcon },
    { id: 'security', name: 'Security Analysis', icon: ShieldCheckIcon },
    { id: 'permissions', name: 'Permissions', icon: KeyIcon },
    { id: 'configuration', name: 'Configuration', icon: CogIcon },
    { id: 'compliance', name: 'Compliance', icon: BadgeCheckIcon },
  ];

  return (
    <>
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/teams-apps')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Teams Apps
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row items-center justify-between mb-6">
        <div className="w-full md:w-1/3">
          <div className="flex items-center justify-start md:justify-start">
            <div className="w-1/4">
              <img
                src={app.icon || "/mobstr_logo.png"}
                className="rounded-2xl align-middle border-none max-w-full h-auto"
                alt={`${app.name} App Icon`}
                onError={(e) => {
                  e.target.src = "/mobstr_logo.png";
                }}
              />
            </div>
            <div className="w-3/4 pl-3">
              <h2 className="text-xl md:text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate flex items-center">
                {app.securityComplianceStatus === 'Attested' && (
                  <BadgeCheckIcon className="text-green-600 h-5 w-5 mr-2" />
                )}
                {app.name}
              </h2>
              <h5 className="text-md md:text-lg leading-7 text-gray-900 sm:text-lg sm:truncate">
                {app.developer}
              </h5>
              <h5 className="text-md md:text-lg leading-7 text-gray-900 sm:text-lg sm:truncate">
                Version {app.version}
              </h5>
              {app.securityComplianceStatus && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${
                  app.securityComplianceStatus === 'Attested' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                  {app.securityComplianceStatus}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="w-full md:w-2/3 flex flex-wrap space-y-2 mt-4 md:mt-0">
          <div className="w-full flex justify-end text-right space-x-2">
            {app.developerUrl && (
            <span className="mb-2">
                <a href={app.developerUrl} target="_blank" rel="noopener noreferrer">
                <button
                  type="button"
                  className="text-white bg-[#2c4251] hover:bg-[#3d627a] font-medium rounded text-sm w-full sm:w-auto px-5 py-2.5 text-center uppercase"
                >
                  <LinkIcon
                    className="-ml-1 mr-2 h-5 w-5 inline"
                    aria-hidden="true"
                  />
                    View Developer Site
                </button>
              </a>
            </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={classNames(
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'
              )}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* App Information */}
          <div className="bg-white overflow-hidden sm:rounded-lg mb-6 border-solid border-gray-200 border">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex mb-8">
            <div className="w-10/12">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-3">
                Teams Application Information
              </h3>
              <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <ServerIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  Microsoft Teams App
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <CogIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                      Manifest v{app.manifestVersion}
                    </div>
                    <div className="mt-2 flex items-center text-sm text-gray-500">
                      <ClockIcon
                        className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                      Updated {formatDate(app.updatedAt)}
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <CalendarIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                      Scanned {formatDate(app.lastScanned)}
                </div>
              </div>
            </div>
            <div className="w-2/12 flex justify-end items-center h-auto">
              <div className="flex items-center flex-col relative">
                <div style={{ width: 130 }}>
                      <Gauge value={latestScan?.overallScore || 0} />
                </div>
                <div className="absolute top-0 right-0">
                  <InformationCircleIcon
                    className="h-5 w-5 text-gray-500 cursor-pointer"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  />
                  {showTooltip && (
                    <div
                          className="absolute top-0 right-full mt-1 p-2 bg-gray-100 rounded-md shadow-md text-sm z-10"
                      style={{ minWidth: "500px" }}
                    >
                      <p className="text-sm text-gray-600">
                        <ul>
                              <li><strong>A*</strong> - Outstanding security (95-100)</li>
                              <li><strong>A</strong> - Excellent security (85-94)</li>
                              <li><strong>B</strong> - Good security (75-84)</li>
                              <li><strong>C</strong> - Acceptable security (65-74)</li>
                              <li><strong>D</strong> - Poor security (55-64)</li>
                              <li><strong>F</strong> - Critical security issues (0-54)</li>
                        </ul>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {app.description || app.shortDescription || "No description available"}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">App ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">
                      {app.appId}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Categories</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {app.categories?.join(", ") || "No categories specified"}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Supported Hubs</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {app.supportedHubs?.join(", ") || "Teams"}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Trust Level</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <div className="flex items-center space-x-2">
                        <span>{app.isFullTrust ? "Full Trust" : "Standard Trust"}</span>
                        {app.isMicrosoftOwned && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Microsoft Owned
                          </span>
                        )}
                      </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Security Score Breakdown
          {latestScan && (
            <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mb-6">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Security Score Breakdown
                </h3>
              </div>
              <div className="border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
                  {Object.entries(latestScan.categoryScores || {}).map(([key, value], index) => {
                    const categoryNames = {
                      scoreManifestCompliance: "Manifest Compliance",
                      scoreDomainSecurity: "Domain Security",
                      scorePermissions: "Permissions",
                      scoreBotSecurity: "Bot Security",
                      scoreTabSecurity: "Tab Security",
                      scoreTrustLevel: "Trust Level",
                      scoreAuthentication: "Authentication",
                      scoreDeveloperTrust: "Developer Trust",
                      scoreCompliance: "Compliance",
                      scoreDevicePermissions: "Device Permissions"
                    };

                    const getScoreGrade = (score) => {
                      if (score >= 95) return "A*";
                      if (score >= 85) return "A";
                      if (score >= 75) return "B";
                      if (score >= 65) return "C";
                      if (score >= 55) return "D";
                      return "F";
                    };

                    const getScoreColor = (score) => {
                      if (score >= 85) return "text-green-600 bg-green-100";
                      if (score >= 75) return "text-yellow-600 bg-yellow-100";
                      if (score >= 65) return "text-orange-600 bg-orange-100";
                      return "text-red-600 bg-red-100";
                    };

                    const getProgressWidth = (score) => {
                      return Math.max(0, Math.min(100, score || 0));
                    };
                    
                    return (
                      <div key={key} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">
                            {categoryNames[key] || key}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColor(value)}`}>
                            {getScoreGrade(value || 0)}
                          </span>
                  </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              value >= 85 ? "bg-green-500" :
                              value >= 75 ? "bg-yellow-500" :
                              value >= 65 ? "bg-orange-500" : "bg-red-500"
                            }`}
                            style={{ width: `${getProgressWidth(value)}%` }}
                          ></div>
                </div>
              </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )} */}
        </>
      )}

      {/* Security Analysis Tab */}
      {activeTab === 'security' && latestScan && (
        <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Security Findings
          </h3>
          <p className="text-sm text-gray-600">
              Detailed security analysis findings from the latest scan.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm text-gray-500 font-bold">Finding</dt>
              <dt className="text-sm text-gray-500 font-bold">Severity</dt>
              <dt className="text-sm text-gray-500 font-bold">Details</dt>
            </div>

              {latestScan.findings?.length > 0 ? (
                latestScan.findings.map((finding, index) => (
              <div key={index}>
                <div className={classNames(
                      finding.severity === "High" ? "bg-red-50" : 
                      finding.severity === "Medium" ? "bg-yellow-50" : "bg-green-50",
                      "px-4 py-5 sm:grid grid-cols-3 sm:gap-4 sm:px-6 relative"
                )}>
                  <div className="text-sm">
                        <span className="font-bold text-gray-900 block">{finding.name}</span>
                    <span className="text-xs text-gray-500 block mt-1">
                          Category: {finding.category}
                    </span>
                        {finding.checkId && (
                    <span className="text-xs text-gray-500 block">
                            Check ID: {finding.checkId}
                    </span>
                        )}
                  </div>
                  
                  <div className="text-center">
                    <span className={classNames(
                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                          finding.severity === "High" ? "bg-red-100 text-red-800" :
                          finding.severity === "Medium" ? "bg-yellow-100 text-yellow-800" :
                      "bg-green-100 text-green-800"
                    )}>
                          {finding.severity === "High" && <ExclamationIcon className="w-4 h-4 mr-1" />}
                          {finding.severity}
                    </span>
                        {finding.riskScore && (
                    <div className="text-xs text-gray-500 mt-1">
                            Risk Score: {finding.riskScore}/10
                    </div>
                        )}
                  </div>

                  <div className="text-sm text-gray-700">
                        <p className="mb-2">{finding.finding}</p>
                        {finding.recommendation && (
                    <p className="text-xs">
                            <strong>Recommendation:</strong> {finding.recommendation}
                          </p>
                        )}
                  </div>

                  <button
                        onClick={() => toggleRow(`finding-${index}`)}
                    className="absolute right-4 top-4"
                  >
                        {expandedRows[`finding-${index}`] ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                </div>
                
                    {expandedRows[`finding-${index}`] && (
                  <div className="bg-gray-100 px-4 py-5 sm:px-6">
                    <h4 className="text-sm font-bold mb-3">Detailed Analysis</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                            <h5 className="font-bold mb-1">Finding Details:</h5>
                            <p className="text-gray-700">{finding.finding}</p>
                      </div>
                      <div>
                        <h5 className="font-bold mb-1">Impact Assessment:</h5>
                        <p className="text-gray-700">
                              {finding.severity} severity finding requiring {finding.severity.toLowerCase()} priority attention.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <textarea
                            className="w-full rounded p-2 focus:border-blue-500"
                        placeholder="Add detailed assessment notes and remediation plan..."
                        rows="3"
                        style={{ resize: "vertical" }}
                      />
                      <div className="mt-2 flex justify-end">
                            <button className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded">
                          Save Assessment
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  No security findings available for this scan.
                </div>
              )}
          </dl>
        </div>
      </div>
      )}

      {/* Permissions Tab */}
      {activeTab === 'permissions' && (
        <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Teams Permissions Analysis
          </h3>
          <p className="text-sm text-gray-600">
              Analysis of permissions requested by this Teams app.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid grid-cols-4 sm:gap-4 sm:px-6">
                <dt className="text-sm text-gray-500 font-bold">Permission</dt>
                <dd className="text-sm text-gray-500 font-bold">Risk Level</dd>
                <dd className="text-sm text-gray-500 font-bold col-span-2">Description</dd>
            </div>

              {app.permissions?.length > 0 ? (
                app.permissions.map((permission, index) => {
                  const riskLevel = getPermissionRisk(permission);
                  return (
                    <div key={index}>
                      <div
                        className={classNames(
                          riskLevel === "High" ? "bg-red-50" : 
                          riskLevel === "Medium" ? "bg-yellow-50" : "bg-green-50",
                "px-4 py-5 sm:grid grid-cols-4 sm:gap-4 sm:px-6"
                        )}
                      >
                        <dt className="text-sm text-gray-900 font-mono font-bold">
                          <KeyIcon className="inline h-4 w-4 mr-1" />
                          {permission}
                        </dt>
                <dd className={classNames(
                          getRiskColor(riskLevel),
                          "mt-1 text-sm font-bold sm:mt-0"
                        )}>
                          {riskLevel}
                        </dd>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 col-span-2">
                          {/* Add permission descriptions based on common Teams permissions */}
                          {permission === 'Identity' && "Access user profile information including name, email, and profile picture"}
                          {permission === 'MessageTeamMembers' && "Send direct messages to team members"}
                          {permission === 'MessageChannelMembers' && "Send messages to channel members"}
                          {!['Identity', 'MessageTeamMembers', 'MessageChannelMembers'].includes(permission) && 
                            `Permission to access ${permission} functionality`}
                        </dd>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  No permissions specified for this app.
                </div>
              )}

              {/* Device Permissions */}
              {app.devicePermissions?.length > 0 && (
                <>
                  <div className="bg-gray-100 px-4 py-3">
                    <h4 className="text-sm font-bold text-gray-900">Device Permissions</h4>
                  </div>
                  {app.devicePermissions.map((permission, index) => (
                    <div key={`device-${index}`} className="bg-blue-50 px-4 py-5 sm:grid grid-cols-4 sm:gap-4 sm:px-6">
                      <dt className="text-sm text-gray-900 font-mono font-bold">
                        <FingerPrintIcon className="inline h-4 w-4 mr-1" />
                        {permission}
                      </dt>
                      <dd className="mt-1 text-sm font-bold text-orange-600 sm:mt-0">
                        Device
                      </dd>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 col-span-2">
                        Device-level permission for {permission.toLowerCase()} access
                </dd>
              </div>
            ))}
                </>
              )}
          </dl>
        </div>
      </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'configuration' && (
        <div className="space-y-6">
          {/* Valid Domains */}
          <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Valid Domains Configuration
              </h3>
              <p className="text-sm text-gray-600">
                Domains that this Teams app is configured to interact with.
              </p>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm text-gray-500 font-bold">Domain</dt>
                  <dd className="text-sm text-gray-500 font-bold">Type</dd>
                  <dd className="text-sm text-gray-500 font-bold">Risk Assessment</dd>
                </div>

                {app.validDomains?.length > 0 ? (
                  app.validDomains.map((domain, index) => {
                    const isWildcard = domain.includes('*');
                    const riskLevel = isWildcard ? (domain.includes('*.*') ? 'High' : 'Medium') : 'Low';
                    
                    return (
                      <div key={index} className={classNames(
                        getRiskBg(riskLevel),
                        "px-4 py-5 sm:grid grid-cols-3 sm:gap-4 sm:px-6"
                      )}>
                        <dt className="text-sm font-mono text-gray-900">
                          <GlobeIcon className="inline h-4 w-4 mr-1" />
                          {domain}
                        </dt>
                        <dd className="text-sm text-gray-700">
                          {isWildcard ? 'Wildcard Domain' : 'Specific Domain'}
                        </dd>
                        <dd className={classNames(getRiskColor(riskLevel), "text-sm font-bold")}>
                          {riskLevel} Risk
                          {isWildcard && (
                            <div className="text-xs text-gray-600 font-normal mt-1">
                              Wildcard patterns may allow access to unintended subdomains
                            </div>
                          )}
                        </dd>
                      </div>
                    );
                  })
                ) : (
                  <div className="px-4 py-8 text-center text-gray-500">
                    No valid domains configured for this app.
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* App Configuration Details */}
          <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Application Configuration
              </h3>
            </div>
            <div className="border-t border-gray-200">
              <dl>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Manifest Version</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {app.manifestVersion}
                  </dd>
                </div>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Default Install Scope</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {app.defaultInstallScope || "Not specified"}
                  </dd>
                </div>
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Block Until Admin Action</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {app.defaultBlockUntilAdminAction ? "Yes" : "No"}
                  </dd>
                </div>
                {app.defaultGroupCapability && (
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Group Capabilities</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <pre className="text-xs bg-gray-100 p-2 rounded">
                        {JSON.stringify(app.defaultGroupCapability, null, 2)}
                      </pre>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Compliance & Documentation
          </h3>
            <p className="text-sm text-gray-600">
              Compliance status and documentation links for this Teams app.
            </p>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Security Compliance Status</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    app.securityComplianceStatus === 'Attested' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {app.securityComplianceStatus || "Not specified"}
                  </span>
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Privacy Policy</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {app.privacyUrl ? (
                    <a href={app.privacyUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                      View Privacy Policy
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Terms of Use</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {app.termsOfUseUrl ? (
                    <a href={app.termsOfUseUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                      View Terms of Use
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Publisher Documentation</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {app.publisherDocsUrl ? (
                    <a href={app.publisherDocsUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                      View Documentation
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </dd>
              </div>
            </dl>
              </div>
            </div>
      )}

      
    </>
  );
}